<template>
  <div class="animated fadeIn">
    <div v-if="initializing" class="text-center">
      <font-awesome-icon spin icon="circle-notch" class="text-moremuted display-2"/>
    </div>

    <template v-else>
      <b-alert :show="errors.any()" variant="danger" class="px-4">
        <ul class="m-0 px-1">
          <li v-for="err in errors.all()" :key="err">
            {{ err }}
          </li>
        </ul>
      </b-alert>

      <b-row>
        <b-col lg="6" class="mb-3">
          <b-card :header="$t('shared.identityData')" class="mb-0 h-100">
            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('shared.name') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('shared.name')"
                  v-validate="'required|max:50'"
                  v-model="model.name"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.managerName') }}

                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('inventories.managerName')"
                  v-validate="'required|max:50'"
                  v-model="model.managerName"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.managerNationalCode') }}

                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('inventories.managerNationalCode')"
                  v-validate="'required|digits:10'"
                  v-model="model.managerNationalCode"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.operatorName') }}

                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('inventories.operatorName')"
                  v-validate="'required|max:50'"
                  v-model="model.operatorName"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.businessLicenseNo') }}
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('inventories.businessLicenseNo')"
                  v-validate="'max:20'"
                  v-model="model.businessLicenseNo"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <date-picker
                :label="$t('inventories.businessLicenseDate')"
                :name="$t('inventories.businessLicenseDate')"
                v-model="model.businessLicenseDate"
              />
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.nationalId') }}

                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('inventories.nationalId')"
                  v-validate="'required|digits:11'"
                  v-model="model.nationalId"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.economicalCode') }}
                </b-input-group-prepend>

                <b-form-input
                  dir="auto"
                  :name="$t('inventories.economicalCode')"
                  v-validate="'digits:12'"
                  v-model="model.economicalCode"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group :prepend="$t('inventories.capacity')" :append="$t('inventories.kilogram')">
                <numeric-input
                  :name="$t('inventories.capacity')"
                  v-model="model.capacity"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group class="mb-0">
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.workingHours') }}

                  <required-symbol/>
                </b-input-group-prepend>

                <b-textarea
                  :rows="3"
                  v-model="model.workingHours"
                  :name="$t('inventories.workingHours')"
                  v-validate="'required|max:400'"
                />
              </b-input-group>
            </b-form-group>
          </b-card>
        </b-col>

        <b-col lg="6" class="mb-3">
          <b-card :header="$t('inventories.officialData')" class="mb-0 h-100">
            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.phoneCode') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  autocomplete="off"
                  :name="$t('inventories.phoneCode')"
                  v-validate="'required|numeric|max:4'"
                  v-model="model.phoneCode"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('inventories.phoneNumber') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  autocomplete="off"
                  :name="$t('inventories.phoneNumber')"
                  v-validate="'required|numeric|max:8'"
                  v-model="model.phoneNumber"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <auto-complete
                :prepend="$t('provinces.province')"
                :select="provinceSelect"
                :search="provinceSearch"
                :placeholder="$t('provinces.enterProvince')"
                :name="$t('provinces.province')"
                v-validate="'required'"
                v-model="model.provinceId"
                required
              />
            </b-form-group>

            <b-form-group>
              <auto-complete
                :disabled="!model.provinceId"
                :prepend="$t('cities.city')"
                :select="citySelect"
                :search="citySearch"
                :placeholder="$t('cities.enterCity')"
                :name="$t('cities.city')"
                v-validate="'required'"
                v-model="model.cityId"
                required
              />
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('account.address') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-textarea
                  :rows="3"
                  v-model="model.address"
                  :name="$t('account.address')"
                  v-validate="'required|max:200'"
                />
              </b-input-group>
            </b-form-group>

            <b-form-group>
              <b-input-group>
                <b-input-group-prepend is-text>
                  {{ $t('account.zipCode') }}
                  <required-symbol/>
                </b-input-group-prepend>

                <b-form-input
                  dir="ltr"
                  :name="$t('account.zipCode')"
                  v-validate="'required|digits:10'"
                  v-model="model.zipCode"
                />
              </b-input-group>
            </b-form-group>

            <geopoint-editor
              required
              :label="$t('shared.coordinates')"
              autoDetect
              :name="$t('shared.coordinates')"
              v-validate="'required'"
              v-model="model.coordinates"
            />
          </b-card>
        </b-col>
      </b-row>

      <b-card :header="$t('shared.documents.documents')" class="h-100">
        <b-form-group>
          <uploader
            required
            :label="$t('inventories.valueAddedRegCert')"
            :name="$t('inventories.valueAddedRegCert')"
            v-validate="'required'"
            v-model="model.valueAddedRegCert"
          />
        </b-form-group>

        <b-form-group class="mb-0">
          <uploader
            :label="$t('inventories.businessLicense')"
            v-model="model.businessLicense"
          />
        </b-form-group>
      </b-card>

      <b-card bg-variant="light">
        <b-row class="align-items-center">
          <b-col lg="auto" class="mb-3 mb-lg-0">
            <b-btn block variant="success" :disabled="progressing" @click="submit">
              <font-awesome-icon
                :icon="progressing ? 'cog' : 'check'"
                :spin="progressing"
                class="align-middle"
                :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
              />

              {{ $t('shared.save') }}
            </b-btn>
          </b-col>

          <b-col lg="auto" class="mb-3 mb-lg-0">
            <b-btn block variant="success" :disabled="progressing" @click="submitAndReviewRequest">
              <font-awesome-icon
                :icon="progressing ? 'cog' : 'check'"
                :spin="progressing"
                class="align-middle"
                :class="{'mr-1': $dir.ltr, 'ml-1': $dir.rtl}"
              />

              {{ $t('shared.saveAndReviewRequest') }}
            </b-btn>
          </b-col>

          <b-col v-if="status" lg="auto" :class="{ 'ml-auto': $dir.ltr, 'mr-auto': $dir.rtl }">
            <span class="text-muted">
              {{ $t('shared.status') }} :
            </span>

            <font-awesome-icon
              :icon="status.icon"
              class="align-middle mx-1"
              :class="`text-${status.variant}`"
            />
            {{ status.text }}
          </b-col>
        </b-row>
      </b-card>
    </template>
  </div>
</template>

<script>
import Breadcrumb from '@/mixins/breadcrumb'
import AutoComplete from '@/components/shared/AutoComplete'
import DatePicker from '@/components/shared/DatePicker'
import Settings from '@/mixins/settings/index'
import Uploader from '@/components/shared/Uploader'
import GeopointEditor from '@/components/shared/GeopointEditor'

export default {
  name: 'BasicInfo',
  components: {
    AutoComplete,
    DatePicker,
    Uploader,
    GeopointEditor
  },
  mixins: [Breadcrumb, Settings],
  data () {
    return {
      initializing: true,
      progressing: false,
      model: {
        address: null,
        businessLicense: null,
        businessLicenseDate: null,
        businessLicenseNo: null,
        capacity: null,
        cityId: null,
        coordinates: null,
        economicalCode: null,
        managerName: null,
        managerNationalCode: null,
        name: null,
        nationalId: null,
        operatorName: null,
        phoneCode: null,
        phoneNumber: null,
        provinceId: null,
        sortOrder: 1000,
        valueAddedRegCert: null,
        workingHours: null,
        zipCode: null,
        status: null
      },
      statuses: [
        { value: 0, text: this.$t('inventories.statuses.filling'), icon: 'pencil-alt', variant: 'info' },
        { value: 1, text: this.$t('inventories.statuses.pending'), icon: 'user-clock', variant: 'muted' },
        { value: 2, text: this.$t('inventories.statuses.approved'), icon: 'check-circle', variant: 'success' },
        { value: 3, text: this.$t('inventories.statuses.disapproved'), icon: 'times-circle', variant: 'danger' }
      ]
    }
  },
  computed: {
    status () {
      return this.statuses.find(o => o.value === this.model.status)
    }
  },
  watch: {
    'model.provinceId' (val, oldVal) {
      if (!this.initializing && val !== oldVal) this.model.provinceId = null
    }
  },
  mounted () {
    Promise.all([
      this.getModel(),
      this.getSettings(`${this.$config.auth.userType}Account`)
    ])
    .then(() => {
      this.setBreadcrumb()
      this.initializing = false
    })
    .catch(err => {
      this.fail(err)
      this.goto('/account/profile')
    })
  },
  methods: {
    setBreadcrumb () {
      const items = []
      let path = ''

      path += '/account'
      items.push({ path, text: this.$t('nav.account') })

      path += '/profile/basicInfo'
      items.push({ path, text: this.$t('inventories.basicData.basicData') })

      this.commitBreadcrumb(items)
    },
    getModel () {
      return this.$api.account.getProfile({ include: 'InventoryData' }, true)
      .then(res => {
        this.model.address = res.profile.inventoryData.address
        this.model.businessLicense = res.profile.inventoryData.businessLicense
        this.model.businessLicenseDate = res.profile.inventoryData.businessLicenseDate
        this.model.businessLicenseNo = res.profile.inventoryData.businessLicenseNo
        this.model.capacity = res.profile.inventoryData.capacity
        this.model.cityId = res.profile.inventoryData.cityId
        this.model.coordinates = res.profile.inventoryData.coordinates
        this.model.economicalCode = res.profile.inventoryData.economicalCode
        this.model.managerName = res.profile.inventoryData.managerName
        this.model.managerNationalCode = res.profile.inventoryData.managerNationalCode
        this.model.name = res.profile.inventoryData.name
        this.model.nationalId = res.profile.inventoryData.nationalId
        this.model.operatorName = res.profile.inventoryData.operatorName
        this.model.phoneCode = res.profile.inventoryData.phoneCode
        this.model.phoneNumber = res.profile.inventoryData.phoneNumber
        this.model.provinceId = res.profile.inventoryData.provinceId
        this.model.sortOrder = res.profile.inventoryData.sortOrder
        this.model.valueAddedRegCert = res.profile.inventoryData.valueAddedRegCert
        this.model.workingHours = res.profile.inventoryData.workingHours
        this.model.zipCode = res.profile.inventoryData.zipCode
        this.model.status = res.profile.inventoryData.status

        return this.model
      })
    },
    fail (err) {
      this.$showError(this.$getLocaleErrorMessage(err, 'account'))
      this.progressing = false
      return false
    },
    goto (path) {
      if (path) this.$router.push({ path })
    },
    provinceSearch (keyword, count) {
      return this.$api.provinces.getInfos({
        name: keyword,
        limit: count,
        sort: 'sortOrder-asc'
      })
      .then(items => {
        return items.map(item => {
          return {
            text: item.name,
            value: item.id
          }
        })
      })
    },
    provinceSelect (value) {
      return this.$api.provinces.getInfos({
        id: value
      })
      .then(items => {
        return {
          text: items[0].name,
          value: items[0].id
        }
      })
    },
    citySearch (keyword, count) {
      return this.$api.cities.getInfos({
        name: keyword,
        limit: count,
        provinceId: this.model.provinceId,
        sort: 'sortOrder-asc'
      })
      .then(items => {
        return items.map(item => {
          return {
            text: item.name,
            value: item.id
          }
        })
      })
    },
    citySelect (value) {
      return this.$api.cities.getInfos({
        id: value
      })
      .then(items => {
        return {
          text: items[0].name,
          value: items[0].id
        }
      })
    },
    preSubmit () {
      this.$validator.validateAll()
      .then(isValid => {
        this.progressing = isValid
        return isValid
      })
    },
    _submit () {
      this.progressing = true
      return this.$api.account.updateInventoryData(this.model, true)
      .then(res => {
        this.model.valueAddedRegCert = res.valueAddedRegCert
        this.model.businessLicense = res.businessLicense
        this.model.status = res.status
      })
    },
    reviewRequest () {
      return this.$api.account.inventoryDataApprovementRequest(false)
    },
    submit () {
      this.preSubmit()
      .then(proceed => {
        if (proceed) {
          this.progressing = true
          return this._submit()
          .then(() => {
            return this.$showSuccess(this.$t('account.profileUpdated'))
          })
        }
      })
      .catch(err => {
        return this.$showError(this.$getLocaleErrorMessage(err, 'inventories'))
      })
      .then(() => { this.progressing = false })
    },
    submitAndReviewRequest () {
      this.preSubmit()
      .then(proceed => {
        if (proceed) {
          this.progressing = true
          return this._submit()
          .then(this.reviewRequest)
          .then(() => {
            this.model.status = 1
            this.$showSuccess(`${this.$t('account.profileUpdated')} ${this.$t('inventories.waitForAdminApproval')}`)
          })
        }
      })
      .catch(err => {
        return this.$showError(this.$getLocaleErrorMessage(err, 'inventories'))
      })
      .then(() => { this.progressing = false })
    }
  }
}
</script>
